import type { Feature, FeatureCollection, Point, Polygon } from "geojson";
import {
  difference,
  type MultiPolygon,
  type Polygon as TClippingPolygon,
  type Ring as TClippingRing,
} from "polygon-clipping";

import { HALF_FACTOR, IGeoJSON, IGeoJSONFeature } from "@ryde/shared";

import { POLYGON_EDITOR_MIN_RING_VERTICES } from "./maplibre-polygon-editor.constants";
import {
  IEditablePolygon,
  IPolygonVertexRef,
  TPolygonEditorMode,
  TPolygonPosition,
  TPolygonRing,
} from "./maplibre-polygon-editor.models";

/** A ring of fewer than three distinct corners encloses nothing and cannot be edited. */
const hasEnoughVertices = (ring: TPolygonRing): boolean =>
  ring.length >= POLYGON_EDITOR_MIN_RING_VERTICES;

/** Halfway between two ordinates — where a midpoint handle sits on its edge. */
const midpointOf = (from: number, to: number): number =>
  (from + to) / HALF_FACTOR;

const arePositionsEqual = (
  [aLng, aLat]: TPolygonPosition,
  [bLng, bLat]: TPolygonPosition,
): boolean => aLng === bLng && aLat === bLat;

/** Strips the repeated closing point GeoJSON requires, so each corner appears exactly once. */
export const openRing = (ring: TPolygonRing): TPolygonRing => {
  const [first] = ring;
  const last = ring.at(-1);

  if (ring.length > 1 && first && last && arePositionsEqual(first, last)) {
    return ring.slice(0, -1);
  }

  return [...ring];
};

/** Puts it back for anything that reads GeoJSON. */
export const closeRing = (ring: TPolygonRing): TPolygonRing => {
  const [first] = ring;
  const last = ring.at(-1);

  if (!first || !last || arePositionsEqual(first, last)) {
    return [...ring];
  }

  return [...ring, first];
};

/**
 * The ring sets a stored geometry contributes, one entry per editable shape.
 *
 * A MultiPolygon has to be flattened here rather than skipped: the cut tool saves a shape it split as a
 * MultiPolygon, and areas persisted that way — including admin GeoAreas whose `geom` is a MultiPolygon —
 * would otherwise open in an empty editor and lose their geometry on the next save. The Leaflet path
 * flattened on the way out; this flattens on the way in.
 *
 * `IGeoJSONFeature` models only Point / LineString / Polygon, so the MultiPolygon case is recognised from
 * the wire shape rather than from the declared type.
 */
const toRingSets = (
  geometry: IGeoJSONFeature["geometry"],
): TPolygonRing[][] => {
  if (geometry?.type === "Polygon") {
    return [geometry.coordinates];
  }

  if ((geometry as { type?: string })?.type === "MultiPolygon") {
    return (geometry as unknown as { coordinates: TPolygonRing[][] })
      .coordinates;
  }

  return [];
};

/**
 * Reads whatever the form holds into editable shapes. A Polygon becomes one shape; a MultiPolygon becomes
 * one per member, which is how a shape that was cut in two comes back as two independently editable ones.
 */
export const toEditablePolygons = (
  geoJson: IGeoJSON | undefined,
  createId: () => string,
): IEditablePolygon[] =>
  (geoJson?.features ?? []).flatMap(feature =>
    toRingSets(feature.geometry).flatMap(ringSet => {
      const rings = ringSet.map(openRing).filter(hasEnoughVertices);

      return rings.length ? [{ id: createId(), rings }] : [];
    }),
  );

/**
 * The polygon's rings, closed and with any too short to enclose anything dropped. Empty when none
 * qualify, which is how both builders below decide to skip the polygon entirely — one walk of the rings
 * rather than a `some` to test and a `filter` to repeat it.
 */
const toClosedRings = ({ rings }: IEditablePolygon): TPolygonPosition[][] =>
  rings.filter(hasEnoughVertices).map(closeRing);

/**
 * Writes them back out as the one-feature-per-polygon collection the forms expect — the same shape
 * `toPolygonFeatures` produced from a Leaflet layer. No properties: this is what gets persisted.
 */
export const toEditorGeoJson = (polygons: IEditablePolygon[]): IGeoJSON => ({
  type: "FeatureCollection",
  features: polygons.flatMap((polygon): IGeoJSONFeature[] => {
    const coordinates = toClosedRings(polygon);

    return coordinates.length
      ? [
          {
            type: "Feature",
            properties: {},
            geometry: { type: "Polygon", coordinates },
          },
        ]
      : [];
  }),
});

/** The shapes themselves, for the fill and line layers, keyed by id so they can be hit-tested. */
export const buildEditorShapesCollection = (
  polygons: IEditablePolygon[],
): FeatureCollection<Polygon> => ({
  type: "FeatureCollection",
  features: polygons.flatMap((polygon): Feature<Polygon>[] => {
    const coordinates = toClosedRings(polygon);

    return coordinates.length
      ? [
          {
            type: "Feature",
            properties: { polygonId: polygon.id },
            geometry: { type: "Polygon", coordinates },
          },
        ]
      : [];
  }),
});

const toHandleFeature = (
  position: TPolygonPosition,
  { polygonId, ringIndex, vertexIndex }: IPolygonVertexRef,
): Feature<Point> => ({
  type: "Feature",
  properties: { polygonId, ringIndex, vertexIndex },
  geometry: { type: "Point", coordinates: position },
});

/** One draggable handle per corner. */
export const buildEditorVerticesCollection = (
  polygons: IEditablePolygon[],
): FeatureCollection<Point> => ({
  type: "FeatureCollection",
  features: polygons.flatMap(({ id, rings }) =>
    rings.flatMap((ring, ringIndex) =>
      ring.map((position, vertexIndex) =>
        toHandleFeature(position, {
          polygonId: id,
          ringIndex,
          vertexIndex,
        }),
      ),
    ),
  ),
});

/**
 * One smaller handle halfway along every edge. Dragging it inserts a corner there — geoman's middle
 * markers. `vertexIndex` is where the new corner goes, which is after the edge's start.
 */
export const buildEditorMidpointsCollection = (
  polygons: IEditablePolygon[],
): FeatureCollection<Point> => ({
  type: "FeatureCollection",
  features: polygons.flatMap(({ id, rings }) =>
    // Mapped rather than filtered: ringIndex has to stay the index in the shape's own ring list, since
    // that is what an insert is applied to.
    rings.flatMap((ring, ringIndex) =>
      !hasEnoughVertices(ring)
        ? []
        : ring.map((position, index) => {
            const next = ring[(index + 1) % ring.length];

            return toHandleFeature(
              [
                midpointOf(position[0], next[0]),
                midpointOf(position[1], next[1]),
              ],
              { polygonId: id, ringIndex, vertexIndex: index + 1 },
            );
          }),
    ),
  ),
});

/**
 * A ring traced by the lasso arrives as [lat, lng] pairs, which is Leaflet's order; the editor works in
 * GeoJSON order throughout.
 */
export const toEditorRing = (traced: [number, number][]): TPolygonRing =>
  openRing(traced.map(([lat, lng]): TPolygonPosition => [lng, lat]));

/** Every ring moved by the same delta — a rigid translation, so the shape cannot deform. */
export const translatePolygonRings = (
  rings: TPolygonRing[],
  deltaLng: number,
  deltaLat: number,
): TPolygonRing[] =>
  rings.map(ring =>
    ring.map(([lng, lat]): TPolygonPosition => [
      lng + deltaLng,
      lat + deltaLat,
    ]),
  );

/** Cross product of (b - a) and (c - a); its sign says which side of ab the point c falls on. */
const crossProductSign = (
  a: TPolygonPosition,
  b: TPolygonPosition,
  c: TPolygonPosition,
): number => (b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]);

/**
 * True only when the two segments cross properly — each strictly straddles the other's line. Segments that
 * merely share an endpoint give a zero cross product and are not a crossing, which is what makes this
 * usable on a ring where consecutive edges always touch.
 */
export const doSegmentsProperlyIntersect = (
  a1: TPolygonPosition,
  a2: TPolygonPosition,
  b1: TPolygonPosition,
  b2: TPolygonPosition,
): boolean => {
  const d1 = crossProductSign(b1, b2, a1);
  const d2 = crossProductSign(b1, b2, a2);
  const d3 = crossProductSign(a1, a2, b1);
  const d4 = crossProductSign(a1, a2, b2);

  return (
    ((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
    ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))
  );
};

interface IVertexMoveSelfIntersectArgs {
  ring: TPolygonRing;
  vertexIndex: number;
  position: TPolygonPosition;
}

/**
 * Would moving one corner to `position` make the ring cross itself?
 *
 * Only the two edges meeting at that corner change, so only those two need testing — against every other
 * edge. That is O(n) per pointer move. Geoman re-ran a whole-layer intersection scan on every move
 * instead, which is O(n²) and is why reshaping a detailed boundary used to stutter.
 */
export const doesVertexMoveSelfIntersect = ({
  ring,
  vertexIndex,
  position,
}: IVertexMoveSelfIntersectArgs): boolean => {
  const count = ring.length;

  // A triangle has no pair of non-adjacent edges, so it can never cross itself.
  if (count <= POLYGON_EDITOR_MIN_RING_VERTICES) {
    return false;
  }

  const incomingIndex = (vertexIndex - 1 + count) % count;
  const outgoingIndex = vertexIndex;
  const movedEdges: [TPolygonPosition, TPolygonPosition][] = [
    [ring[incomingIndex], position],
    [position, ring[(vertexIndex + 1) % count]],
  ];

  for (let index = 0; index < count; index += 1) {
    if (index === incomingIndex || index === outgoingIndex) {
      continue;
    }

    const from = ring[index];
    const to = ring[(index + 1) % count];

    if (
      movedEdges.some(([edgeFrom, edgeTo]) =>
        doSegmentsProperlyIntersect(edgeFrom, edgeTo, from, to),
      )
    ) {
      return true;
    }
  }

  return false;
};

/** Replaces one corner, leaving every other ring and shape untouched. */
export const withMovedVertex = (
  polygons: IEditablePolygon[],
  { polygonId, ringIndex, vertexIndex }: IPolygonVertexRef,
  position: TPolygonPosition,
): IEditablePolygon[] =>
  polygons.map(polygon =>
    polygon.id !== polygonId
      ? polygon
      : {
          ...polygon,
          rings: polygon.rings.map((ring, index) =>
            index !== ringIndex
              ? ring
              : ring.map((existing, current) =>
                  current === vertexIndex ? position : existing,
                ),
          ),
        },
  );

/** Adds a corner at `vertexIndex` — what dragging a midpoint does. */
export const withInsertedVertex = (
  polygons: IEditablePolygon[],
  { polygonId, ringIndex, vertexIndex }: IPolygonVertexRef,
  position: TPolygonPosition,
): IEditablePolygon[] =>
  polygons.map(polygon =>
    polygon.id !== polygonId
      ? polygon
      : {
          ...polygon,
          rings: polygon.rings.map((ring, index) =>
            index !== ringIndex
              ? ring
              : [
                  ...ring.slice(0, vertexIndex),
                  position,
                  ...ring.slice(vertexIndex),
                ],
          ),
        },
  );

/**
 * Removes a corner, unless doing so would take its ring below three — geoman refused that too, rather
 * than leaving a degenerate shape behind.
 */
export const withRemovedVertex = (
  polygons: IEditablePolygon[],
  { polygonId, ringIndex, vertexIndex }: IPolygonVertexRef,
): IEditablePolygon[] =>
  polygons.map(polygon =>
    polygon.id !== polygonId
      ? polygon
      : {
          ...polygon,
          rings: polygon.rings.map((ring, index) =>
            index !== ringIndex ||
            ring.length <= POLYGON_EDITOR_MIN_RING_VERTICES
              ? ring
              : ring.filter((_, current) => current !== vertexIndex),
          ),
        },
  );

interface ICutEditablePolygonsArgs {
  polygons: IEditablePolygon[];
  cutRing: TPolygonRing;
  createId: () => string;
}

/**
 * Subtracts the traced shape from every shape it overlaps.
 *
 * A cut can split one shape into several or erase it outright, so the result is rebuilt rather than
 * patched. The first piece keeps the original id — a shape that the cut missed comes back unchanged and
 * keeps its identity, so its handles do not flicker.
 */
export const cutEditablePolygons = ({
  polygons,
  cutRing,
  createId,
}: ICutEditablePolygonsArgs): IEditablePolygon[] => {
  const clip = [closeRing(cutRing)];

  return polygons.flatMap(polygon => {
    const subject = polygon.rings.filter(hasEnoughVertices).map(closeRing);

    if (!subject.length) {
      return [polygon];
    }

    const remaining: MultiPolygon | null = difference(subject, clip);

    if (!remaining) {
      return [];
    }

    return remaining.map((rings: TClippingPolygon, index: number) => ({
      id: index === 0 ? polygon.id : createId(),
      rings: rings.map((ring: TClippingRing) => openRing(ring)),
    }));
  });
};

/**
 * A feature's index properties as real numbers, or null if either is missing or not a valid index.
 *
 * Coerced rather than type-tested: `queryRenderedFeatures` returns properties that have been through the
 * worker's serialization, which does not guarantee a numeric index survives as a number. A `typeof`
 * check would make every drag and reshape fail silently on the day it comes back as a string.
 */
const toVertexIndex = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const index = Number(value);

  return Number.isInteger(index) && index >= 0 ? index : null;
};

/** The vertex a rendered handle refers back to, or null if the feature is not one of ours. */
export const toVertexRef = (
  properties: Record<string, unknown> | null,
): IPolygonVertexRef | null => {
  const polygonId = properties?.polygonId;
  const ringIndex = toVertexIndex(properties?.ringIndex);
  const vertexIndex = toVertexIndex(properties?.vertexIndex);

  if (
    typeof polygonId !== "string" ||
    ringIndex === null ||
    vertexIndex === null
  ) {
    return null;
  }

  return { polygonId, ringIndex, vertexIndex };
};

export /** A tool that traces a shape gets the full actions row; the rest only need dismissing. */
const isTracingMode = (mode: TPolygonEditorMode): boolean =>
  mode === "draw" || mode === "cut";
